import { useEffect, useRef, useState } from 'react';
import { AlertCircle, Download, HardDriveDownload, Plus, RotateCcw } from 'lucide-react';
import type { InfraSettingRow } from '@command-room/shared';
import {
  useBackups,
  useCreateBackup,
  useInfraSettings,
  useOllamaModels,
  useUpdateInfraSettings,
} from '@/api/queries';
import { BlockSkeleton, TableSkeleton } from '@/components/feedback/Skeletons';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectItem } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { bytes } from '@/lib/format';


function BackupsTab() {
  const { data: backups, isLoading } = useBackups();
  const createBackup = useCreateBackup();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Backups include a pg_dump, agent config files, and the n8n env file.
        </p>
        <Button
          onClick={() => createBackup.mutate()}
          disabled={createBackup.isPending}
        >
          <Plus className="h-4 w-4" />
          {createBackup.isPending ? 'Creating…' : 'New backup'}
        </Button>
      </div>

      {createBackup.error && (
        <p className="text-sm text-destructive flex items-center gap-1">
          <AlertCircle className="h-4 w-4" />
          {(createBackup.error as Error).message}
        </p>
      )}

      {isLoading ? (
        <TableSkeleton widths={['w-48', 'w-24', 'w-20', 'w-16']} rows={3} />
      ) : !backups?.length ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 gap-2">
            <HardDriveDownload className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No backups yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3">Contents</th>
                <th className="px-4 py-3 text-right">Download</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {backups.map((backup) => (
                <tr key={backup.id} className="hover:bg-muted/20">
                  <td className="px-4 py-3 font-mono text-xs">{backup.id}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(backup.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">{bytes(backup.size_bytes)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {backup.contains.map((c) => (
                        <Badge key={c} variant="secondary">{c}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <a
                      href={`/api/settings/backup/${backup.id}`}
                      download
                      className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Download
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const RESTORE_CHALLENGE = 'restore';

function RestoreTab() {
  const [challenge, setChallenge] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<'idle' | 'uploading' | 'done' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const ready = challenge === RESTORE_CHALLENGE && file !== null;

  async function handleRestore() {
    if (!file || !ready) return;
    setStatus('uploading');
    setErrorMsg('');
    try {
      const form = new FormData();
      form.append('backup', file);
      const res = await fetch('/api/settings/restore', {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Requested-With': 'command-room' },
        body: form,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? res.statusText);
      }
      setStatus('done');
    } catch (err) {
      setStatus('error');
      setErrorMsg((err as Error).message);
    }
  }

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <Card className="border-warning/50">
        <CardContent className="flex gap-3 pt-6">
          <AlertCircle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium">Restore overwrites existing data</p>
            <p className="text-sm text-muted-foreground mt-1">
              The database will be dropped and recreated from the backup dump. All changes made
              after the backup was taken will be lost. n8n will be restarted automatically.
            </p>
          </div>
        </CardContent>
      </Card>

      {status === 'done' ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-success font-medium">
            Restore started — the system will be unavailable for ~30 seconds.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="backup_file">Backup file (.tar.gz)</Label>
            <Input
              id="backup_file"
              type="file"
              accept=".tar.gz,.gz"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {file && (
              <p className="text-xs text-muted-foreground">
                {file.name} · {bytes(file.size)}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="challenge">
              Type <code className="text-xs bg-muted px-1 rounded">{RESTORE_CHALLENGE}</code> to
              confirm
            </Label>
            <Input
              id="challenge"
              value={challenge}
              onChange={(e) => setChallenge(e.target.value)}
              placeholder="restore"
              className={challenge && challenge !== RESTORE_CHALLENGE ? 'border-destructive' : ''}
            />
          </div>

          {status === 'error' && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {errorMsg}
            </p>
          )}

          <Button
            variant="destructive"
            disabled={!ready || status === 'uploading'}
            onClick={handleRestore}
          >
            {status === 'uploading' ? 'Uploading…' : 'Restore from backup'}
          </Button>
        </div>
      )}
    </div>
  );
}

const INFRA_CATEGORIES: { id: string; label: string; keys: (key: string) => boolean }[] = [
  {
    id: 'llm',
    label: 'LLMs',
    // The Ollama address sits next to the model names it serves (2026-10-21).
    keys: (k) => (k.startsWith('n8n_') && k.endsWith('_llm')) || k === 'ollama_base_url',
  },
  {
    id: 'image_generation',
    label: 'Image Generation',
    // Grouped ahead of 'workflows' so image_generation_workflow_id lands
    // here too, next to the model settings it belongs with.
    keys: (k) => k.startsWith('image_generation_'),
  },
  {
    id: 'workflows',
    label: 'Workflows',
    keys: (k) => k.endsWith('_workflow_id'),
  },
  {
    id: 'gdrive',
    label: 'Google Drive',
    keys: (k) => k.startsWith('gdrive_') || k.includes('folder_id') || k === 'parent_drive_id' || k === 'vertex_project_id',
  },
  {
    id: 'slack',
    label: 'Slack',
    keys: (k) => k.includes('_channel_id') || k === 'bot_user_id',
  },
  {
    id: 'sheets',
    label: 'Sheets',
    keys: (k) => k.includes('_sheet_'),
  },
];

// Settings whose value is prose, edited in a textarea. Any other value that is already
// multi-line or long gets one too; the choice is made from the saved value, not the draft,
// so the control never swaps under the cursor while someone types.
const LONG_TEXT_SETTINGS = new Set(['image_generation_hero_prompt']);
const LONG_TEXT_MIN_CHARS = 200;

function isLongText(row: InfraSettingRow): boolean {
  const saved = row.value ?? '';
  return LONG_TEXT_SETTINGS.has(row.key) || saved.includes('\n') || saved.length > LONG_TEXT_MIN_CHARS;
}

function categorizeSetting(key: string): string {
  for (const cat of INFRA_CATEGORIES) {
    if (cat.keys(key)) return cat.id;
  }
  return 'other';
}

function SettingRow({
  row,
  draft,
  onChange,
  dirty,
  options,
}: {
  row: InfraSettingRow;
  draft: string;
  onChange: (v: string) => void;
  dirty: boolean;
  options?: string[];
}) {
  return (
    <div className="flex items-start gap-4 py-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-medium">{row.label ?? row.key}</span>
          <code className="text-[10px] font-mono text-muted-foreground bg-muted px-1 rounded">
            {row.key}
          </code>
        </div>
        {row.description && (
          <p className="text-xs text-muted-foreground mb-2">{row.description}</p>
        )}
        {options ? (
          <Select
            value={draft}
            onValueChange={onChange}
            placeholder="Select a model…"
            className="font-mono text-xs h-8"
          >
            {draft && !options.includes(draft) && (
              <SelectItem value={draft}>{draft} (not installed)</SelectItem>
            )}
            {options.map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </Select>
        ) : isLongText(row) ? (
          <>
            <Textarea
              value={draft}
              onChange={(e) => onChange(e.target.value)}
              rows={16}
              spellCheck={false}
              className="font-mono text-xs leading-relaxed"
            />
            <p className="mt-1 text-right text-[10px] text-muted-foreground">
              {draft.length.toLocaleString()} characters
            </p>
          </>
        ) : (
          <Input
            value={draft}
            onChange={(e) => onChange(e.target.value)}
            className="font-mono text-xs h-8"
          />
        )}
      </div>
      <div className="shrink-0 pt-7 w-20">
        {dirty && (
          <span className="flex items-center gap-1.5 text-xs text-warning">
            <span className="h-1.5 w-1.5 rounded-full bg-warning" />
            Modified
          </span>
        )}
      </div>
    </div>
  );
}

function InfrastructureTab() {
  const { data: settings, isLoading } = useInfraSettings();
  const { data: ollamaModels } = useOllamaModels();
  const localModelNames = ollamaModels?.map((m) => m.name) ?? [];
  const updateSettings = useUpdateInfraSettings();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const savedTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (savedTimeout.current) clearTimeout(savedTimeout.current);
    };
  }, []);

  if (isLoading) return <BlockSkeleton />;

  function valueOf(row: InfraSettingRow): string {
    return drafts[row.key] ?? (row.value ?? '');
  }

  function isDirty(row: InfraSettingRow): boolean {
    return row.key in drafts && drafts[row.key] !== (row.value ?? '');
  }

  const dirtyRows = (settings ?? []).filter(isDirty);

  async function handleSaveAll() {
    if (dirtyRows.length === 0) return;
    const payload = Object.fromEntries(dirtyRows.map((r) => [r.key, drafts[r.key]!]));
    await updateSettings.mutateAsync(payload);
    setDrafts({});
    setSaved(true);
    if (savedTimeout.current) clearTimeout(savedTimeout.current);
    savedTimeout.current = setTimeout(() => setSaved(false), 3000);
  }

  function handleDiscard() {
    setDrafts({});
  }

  const grouped: Record<string, InfraSettingRow[]> = {};
  for (const s of (settings ?? [])) {
    const cat = categorizeSetting(s.key);
    (grouped[cat] ??= []).push(s);
  }

  const tabs = [
    ...INFRA_CATEGORIES.filter((c) => grouped[c.id]?.length),
    ...(grouped['other']?.length ? [{ id: 'other', label: 'Other' }] : []),
  ];

  const dirtyCategoryCounts: Record<string, number> = {};
  for (const row of dirtyRows) {
    const cat = categorizeSetting(row.key);
    dirtyCategoryCounts[cat] = (dirtyCategoryCounts[cat] ?? 0) + 1;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Infrastructure settings</CardTitle>
        <p className="text-sm text-muted-foreground">
          Global configuration shared across all agents. Changes are picked up automatically on
          the next agent invocation.
        </p>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue={tabs[0]?.id ?? 'llm'}>
          <TabsList className="mb-4">
            {tabs.map((t) => (
              <TabsTrigger key={t.id} value={t.id} className="relative">
                {t.label}
                <span className="ml-1.5 text-[10px] text-muted-foreground">
                  {grouped[t.id]?.length}
                </span>
                {(dirtyCategoryCounts[t.id] ?? 0) > 0 && (
                  <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-warning" />
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {tabs.map((t) => {
            const rows = grouped[t.id] ?? [];
            const isLlmTab = t.id === 'llm';
            const cloudRows = isLlmTab ? rows.filter((r) => !r.key.includes('local')) : [];
            const localRows = isLlmTab ? rows.filter((r) => r.key.includes('local')) : [];

            function renderRow(row: InfraSettingRow) {
              const isLocalLlm = isLlmTab && row.key.includes('local');
              return (
                <SettingRow
                  key={row.key}
                  row={row}
                  draft={valueOf(row)}
                  onChange={(v) => setDrafts((d) => ({ ...d, [row.key]: v }))}
                  dirty={isDirty(row)}
                  options={isLocalLlm ? localModelNames : undefined}
                />
              );
            }

            return (
              <TabsContent key={t.id} value={t.id}>
                {isLlmTab ? (
                  <div className="flex flex-col">
                    {cloudRows.length > 0 && (
                      <>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground pb-1 border-b">
                          Cloud
                        </p>
                        <div className="flex flex-col divide-y">
                          {cloudRows.map(renderRow)}
                        </div>
                      </>
                    )}
                    {localRows.length > 0 && (
                      <>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground pb-1 border-b mt-6">
                          Local
                        </p>
                        <div className="flex flex-col divide-y">
                          {localRows.map(renderRow)}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col divide-y">
                    {rows.map(renderRow)}
                  </div>
                )}
              </TabsContent>
            );
          })}
        </Tabs>

        <div className="sticky bottom-0 mt-4 flex items-center gap-3 border-t border-border bg-card pt-3">
          {updateSettings.error && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {(updateSettings.error as Error).message}
            </p>
          )}
          <div className="ml-auto flex items-center gap-3">
            {dirtyRows.length > 0 && (
              <Button type="button" variant="outline" size="sm" onClick={handleDiscard}>
                <RotateCcw className="h-3.5 w-3.5" />
                Discard
              </Button>
            )}
            <Button
              onClick={handleSaveAll}
              disabled={dirtyRows.length === 0 || updateSettings.isPending}
            >
              {updateSettings.isPending
                ? 'Saving…'
                : dirtyRows.length > 0
                ? `Save changes (${dirtyRows.length})`
                : 'Save changes'}
            </Button>
            {saved && dirtyRows.length === 0 && (
              <span className="text-xs text-success">Saved</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function SettingsPage() {
  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">MAS Settings</h1>
        <p className="text-sm text-muted-foreground">
          Infrastructure IDs, LLM models, backups, and system restore.
        </p>
      </header>

      <Tabs defaultValue="infrastructure">
        <TabsList>
          <TabsTrigger value="infrastructure">Infrastructure</TabsTrigger>
          <TabsTrigger value="backups">Backups</TabsTrigger>
          <TabsTrigger value="restore">Restore</TabsTrigger>
        </TabsList>

        <TabsContent value="infrastructure" className="mt-6">
          <InfrastructureTab />
        </TabsContent>
        <TabsContent value="backups" className="mt-6">
          <BackupsTab />
        </TabsContent>
        <TabsContent value="restore" className="mt-6">
          <RestoreTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
